import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Trophy, Users, Eye, ArrowLeft, Loader2, Lock, Crown, Medal,
  ArrowUp, ArrowDown, Minus, Star, RefreshCw, TrendingUp, Award
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import BrandHeader from '../components/BrandHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentPortalPage = () => {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accessGranted, setAccessGranted] = useState(false);
  const [childInfo, setChildInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [childScore, setChildScore] = useState(null);
  const [competitionStatus, setCompetitionStatus] = useState('inactive');
  const [currentRound, setCurrentRound] = useState(0);
  const [isFrozen, setIsFrozen] = useState(false);

  // Poll leaderboard when access granted
  useEffect(() => {
    let interval;
    if (accessGranted && accessCode) {
      const pollLeaderboard = async () => {
        try {
          const [leaderboardRes, scoreRes] = await Promise.all([
            axios.get(`${API_URL}/api/parent/leaderboard/${accessCode}`),
            axios.get(`${API_URL}/api/parent/child-score/${accessCode}`)
          ]);
          
          setLeaderboard(leaderboardRes.data.leaderboard);
          setChildInfo(leaderboardRes.data.child_info);
          setCompetitionStatus(leaderboardRes.data.competition_status);
          setCurrentRound(leaderboardRes.data.current_round);
          setIsFrozen(leaderboardRes.data.is_frozen);
          setChildScore(scoreRes.data);
        } catch (err) {
          if (err.response?.status === 403) {
            setError('Results access period has ended.');
            setAccessGranted(false);
          }
        }
      };
      
      pollLeaderboard();
      // Poll every 5 seconds during competition, 30 seconds if frozen
      const pollInterval = isFrozen ? 30000 : 5000;
      interval = setInterval(pollLeaderboard, pollInterval);
    }
    
    return () => clearInterval(interval);
  }, [accessGranted, accessCode, isFrozen]);

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/parent/access`, {
        access_code: accessCode.toUpperCase()
      });

      setAccessGranted(true);
      setChildInfo({
        participant_id: response.data.participant_id,
        child_name: response.data.child_name,
        display_name: response.data.display_name
      });
      
      // Store access code
      sessionStorage.setItem('parent_access_code', accessCode.toUpperCase());
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Access denied');
    } finally {
      setLoading(false);
    }
  };

  const getPositionIcon = (change) => {
    switch (change) {
      case 'up':
        return <ArrowUp className="w-4 h-4 text-[#22c55e]" />;
      case 'down':
        return <ArrowDown className="w-4 h-4 text-[#ef4444]" />;
      default:
        return <Minus className="w-4 h-4 text-[#64748b]" />;
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-[#face39]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-[#94a3b8]" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-[#d97706]" />;
    return null;
  };

  // Access Gate
  if (!accessGranted) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <BrandHeader />

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md animate-fade-in-up">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#a855f7] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#7c3aed]">
                <Eye className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Parent Portal
              </h1>
              <p className="text-[#64748b]">
                Enter your Parent Access Code to view your child's progress
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 text-red-600 text-sm" data-testid="parent-access-error">
                {error}
              </div>
            )}

            <div className="card-olympiad p-8">
              <form onSubmit={handleAccessSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#475569] mb-2">
                    Parent Access Code
                  </label>
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    className="input-olympiad text-center text-2xl font-mono tracking-widest"
                    placeholder="PAC-XXXXXX"
                    maxLength={12}
                    required
                    data-testid="parent-access-code-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !accessCode}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                  data-testid="parent-access-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      View Leaderboard
                    </>
                  )}
                </button>
              </form>
            </div>

            <p className="text-center text-sm text-[#64748b] mt-6">
              Your access code was sent to your email during registration
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Parent Dashboard
  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      <header className="p-6 bg-gradient-to-r from-[#a855f7] to-[#7c3aed]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <Eye className="w-7 h-7 text-white" />
            </div>
            <div className="text-white">
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Parent Portal
              </h1>
              <p className="text-white/70 text-sm">Watching: {childInfo?.display_name}</p>
            </div>
          </div>
          <div className="text-right text-white">
            <p className="text-sm text-white/70">
              {isFrozen ? 'Final Results' : competitionStatus === 'inactive' ? 'Waiting' : `Round ${currentRound}`}
            </p>
            <p className="font-bold capitalize">{competitionStatus.replace('_', ' ')}</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 max-w-4xl mx-auto w-full">
        {/* Child's Score Card */}
        {childInfo && (
          <div className="card-olympiad p-6 sm:p-8 mb-8 border-2 border-[#a855f7] animate-fade-in-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {childInfo.display_name}'s Progress
              </h2>
              {childInfo.in_top_10 && (
                <span className="flex items-center gap-1 text-[#a855f7] font-semibold">
                  <Star className="w-5 h-5" />
                  Top 10!
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[#f3e8ff] rounded-2xl p-4 text-center">
                <p className="text-sm text-[#7c3aed]">Rank</p>
                <p className="text-3xl font-bold text-[#7c3aed]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  #{childInfo.rank || '-'}
                </p>
              </div>
              <div className="bg-[#fef3c7] rounded-2xl p-4 text-center">
                <p className="text-sm text-[#d97706]">Points</p>
                <p className="text-3xl font-bold text-[#d97706]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {childInfo.total_points || 0}
                </p>
              </div>
              <div className="bg-[#dcfce7] rounded-2xl p-4 text-center">
                <p className="text-sm text-[#16a34a]">Accuracy</p>
                <p className="text-3xl font-bold text-[#16a34a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {childInfo.accuracy || 0}%
                </p>
              </div>
            </div>

            {childScore && childScore.round_scores && Object.keys(childScore.round_scores).length > 0 && (
              <div className="pt-6 border-t border-[#e2e8f0]">
                <h3 className="font-semibold text-[#0f172a] mb-4">Round Breakdown</h3>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(round => {
                    const roundScore = childScore.round_scores[round];
                    return (
                      <div 
                        key={round}
                        className={`p-3 rounded-xl text-center ${
                          roundScore ? 'bg-[#f3e8ff]' : 'bg-[#f1f5f9]'
                        }`}
                      >
                        <p className="text-xs text-[#64748b]">R{round}</p>
                        <p className="font-bold text-[#0f172a]">
                          {roundScore?.total_points || '-'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Certificate Download — visible ONLY after the event has concluded
            (leaderboard frozen OR explicit finished/archived status).
            Avoids the cert appearing while the event is still in progress. */}
        {childInfo && childInfo.participant_id && (
          isFrozen ||
          competitionStatus === 'finished' ||
          competitionStatus === 'complete' ||
          competitionStatus === 'Archived' ||
          competitionStatus === 'archived'
        ) && (
          <div
            className="card-olympiad p-6 sm:p-8 mb-8 border-2 border-[#face39] bg-gradient-to-br from-[#fffdf5] to-[#fef3c7] animate-fade-in-up"
            data-testid="parent-certificate-card"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#face39] flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-[#0f172a]" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {childInfo.rank && childInfo.rank <= 3 ? 'Winner Certificate Ready!' : 'Certificate of Participation'}
                  </h2>
                  <p className="text-sm text-[#64748b] mt-0.5">
                    {childInfo.rank && childInfo.rank <= 3
                      ? `${childInfo.display_name} placed #${childInfo.rank}. Download the certificate as PNG or PDF.`
                      : `Celebrate ${childInfo.display_name}'s achievement — download as PNG or PDF.`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/certificate?participant_id=${childInfo.participant_id}`)}
                className="btn-primary flex items-center gap-2 flex-shrink-0"
                data-testid="parent-download-cert-btn"
              >
                <Award className="w-5 h-5" /> View &amp; Download
              </button>
            </div>
            {/* Sticker buddy — fresh fun cartoon sticker, picked weekly per kid.
                Lives on its own page (/sticker) so the cert card layout stays untouched. */}
            <div className="mt-4 pt-4 border-t border-[#face39]/40 flex items-center justify-between gap-3">
              <p className="text-sm text-[#0f172a]/70">
                <span className="font-semibold">Bonus:</span> grab this week&apos;s surprise sticker buddy.
              </p>
              <button
                onClick={() => navigate(`/sticker?participant_id=${childInfo.participant_id}`)}
                className="px-4 py-2 rounded-lg bg-white border-2 border-[#0f172a] text-[#0f172a] font-bold text-sm hover:bg-[#0f172a] hover:text-white transition-colors flex items-center gap-2 flex-shrink-0"
                data-testid="parent-get-sticker-btn"
              >
                Download Your Sticker
              </button>
            </div>
          </div>
        )}

        {/* Live Leaderboard */}
        <div className="card-olympiad p-6 animate-fade-in-up stagger-1">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {isFrozen ? 'Final Leaderboard' : 'Live Leaderboard'}
            </h2>
            <div className="flex items-center gap-2 text-sm text-[#64748b]">
              <RefreshCw className="w-4 h-4" />
              {isFrozen ? 'Frozen' : 'Updates live'}
            </div>
          </div>

          {leaderboard.length === 0 ? (
            <div className="text-center py-12 text-[#64748b]">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Leaderboard will appear once the competition starts</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((participant, index) => {
                const rank = index + 1;
                const isMyChild = participant.is_my_child;
                
                return (
                  <div
                    key={participant.participant_id}
                    className={`flex items-center justify-between p-4 rounded-2xl transition-all ${
                      isMyChild
                        ? 'bg-[#f3e8ff] border-2 border-[#a855f7] shadow-lg'
                        : rank <= 3
                        ? 'bg-[#fef3c7]'
                        : 'bg-[#f8fafc]'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Position Change */}
                      <div className="w-6 flex justify-center">
                        {getPositionIcon(participant.position_change)}
                      </div>
                      
                      {/* Rank */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                        rank === 1
                          ? 'bg-[#face39] text-[#0f172a]'
                          : rank === 2
                          ? 'bg-[#e2e8f0] text-[#64748b]'
                          : rank === 3
                          ? 'bg-[#fcd9b6] text-[#92400e]'
                          : 'bg-white text-[#64748b] border border-[#e2e8f0]'
                      }`}>
                        {getRankBadge(rank) || rank}
                      </div>
                      
                      {/* Name */}
                      <div>
                        <p className={`font-semibold ${isMyChild ? 'text-[#7c3aed]' : 'text-[#0f172a]'}`}>
                          {participant.display_name}
                          {isMyChild && (
                            <span className="ml-2 px-2 py-0.5 bg-[#a855f7] text-white text-xs rounded-full">
                              Your Child
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-[#64748b]">
                          {participant.accuracy}% accuracy
                        </p>
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        isMyChild ? 'text-[#7c3aed]' : rank <= 3 ? 'text-[#d97706]' : 'text-[#0f172a]'
                      }`}>
                        {participant.total_points}
                      </p>
                      <p className="text-xs text-[#64748b]">points</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* If child not in top 10, show their position */}
          {childInfo && !childInfo.in_top_10 && childInfo.rank && (
            <div className="mt-6 pt-6 border-t-2 border-dashed border-[#e2e8f0]">
              <div className="flex items-center justify-between p-4 rounded-2xl bg-[#f3e8ff] border-2 border-[#a855f7]">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold bg-[#a855f7] text-white">
                    #{childInfo.rank}
                  </div>
                  <div>
                    <p className="font-semibold text-[#7c3aed]">
                      {childInfo.display_name}
                      <span className="ml-2 px-2 py-0.5 bg-[#a855f7] text-white text-xs rounded-full">
                        Your Child
                      </span>
                    </p>
                    <p className="text-xs text-[#64748b]">
                      {childInfo.accuracy}% accuracy
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#7c3aed]">{childInfo.total_points}</p>
                  <p className="text-xs text-[#64748b]">points</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-[#eff6ff] rounded-2xl border-2 border-[#bfdbfe]">
          <p className="text-sm text-[#2565eb] text-center">
            <strong>Note:</strong> Only first name and last initial are shown to protect participant privacy.
            {isFrozen && ' Results will be available for 7 days.'}
          </p>
        </div>
      </main>
    </div>
  );
};

export default ParentPortalPage;
